import { UserType } from "@/constants";
import {
  createQuestionGroupOperatorLicenseTypesLicenseTypeIdQuestionGroupsPost,
  getQuestionGroupsOperatorLicenseTypesLicenseTypeIdQuestionGroupsGet,
} from "@/hera-api/client";
import { getAllLicenseTypeQuestionsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  LicenseQuestionAnswerType,
  QuestionScope,
} from "@/hera-api/client/types.gen";
import { useGovernmentPortalJurisdictionId } from "@/hooks/use-government-portal-jurisdiction-id";
import { isAutoGeneratedQuestion } from "@/lib/auto-generated-questions";
import {
  contactRequirementsForNewNacType,
  contactRequirementsFromApiQuestion,
  deriveAnswerRequiredForContactQuestion,
} from "@/lib/contact-question-requirements";
import { getBrowserExtensionsSetupHref } from "@/lib/court-link-extension-install";
import {
  ADAMS_JUDICI_BROWSER_HELPER_PRESET,
  ALACHUA_LINDAS_BROWSER_HELPER_PRESET,
  ALLEGHENY_DCR_BROWSER_HELPER_PRESET,
  BRADFORD_CIVITEK_BROWSER_HELPER_PRESET,
  BREVARD_BECA_BROWSER_HELPER_PRESET,
  BROWARD_WEB2_BROWSER_HELPER_PRESET,
  BUCKS_PA_COURTPRO_BROWSER_HELPER_PRESET,
  CITRUS_SCORSS_BROWSER_HELPER_PRESET,
  CLARK_OH_COURTVIEW_BROWSER_HELPER_PRESET,
  CLAY_BENCHMARK_BROWSER_HELPER_PRESET,
  COOK_CIVIL_CASE_SEARCH_BROWSER_HELPER_PRESET,
  DAUPHIN_COUNTYSUITE_BROWSER_HELPER_PRESET,
  DELAWARE_PA_CTRACK_BROWSER_HELPER_PRESET,
  DUPAGE_CASE_SEARCH_BROWSER_HELPER_PRESET,
  DUVAL_CORE_BROWSER_HELPER_PRESET,
  ERIE_PA_COURTPRO_BROWSER_HELPER_PRESET,
  HAMILTON_OH_CASE_SEARCH_BROWSER_HELPER_PRESET,
  HILLSBOROUGH_HOVER_BROWSER_HELPER_PRESET,
  LEE_MATRIX_BROWSER_HELPER_PRESET,
  LUZERNE_PA_SEARCHIQS_BROWSER_HELPER_PRESET,
  MARTIN_BENCHMARK_BROWSER_HELPER_PRESET,
  MONTGOMERY_PA_COURTPRO_BROWSER_HELPER_PRESET,
  NJ_CIVIL_SEARCH_BROWSER_HELPER_PRESET,
  NORTHAMPTON_COUNTYSUITE_BROWSER_HELPER_PRESET,
  NY_FCAS_BROWSER_HELPER_PRESET,
  ORANGE_FL_MYECLERK_BROWSER_HELPER_PRESET,
  PEORIA_TYLER_BROWSER_HELPER_PRESET,
  ST_JOHNS_BENCHMARK_BROWSER_HELPER_PRESET,
  TAZEWELL_TYLER_BROWSER_HELPER_PRESET,
  VOLUSIA_CCMS_BROWSER_HELPER_PRESET,
  WESTMORELAND_PA_IJEMS_BROWSER_HELPER_PRESET,
  YORK_COUNTYSUITE_BROWSER_HELPER_PRESET,
} from "@/lib/court-link-helper";
import { getCreateFormFilterDefaults } from "@/lib/create-form-filter-defaults";
import { isGovernmentPortalViewer } from "@/lib/government-impersonate";
import { jurisdictionOptionLabel } from "@/lib/jurisdiction-option-label";
import {
  isListableQuestionAnswerType,
  showInListFormValues,
  showInListHelpText,
} from "@/lib/listable-answer-types";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import {
  BOOLEAN_ANSWER_OPTION_KEYS_ORDER,
  BOOLEAN_ANSWER_OPTION_NO,
  BOOLEAN_ANSWER_OPTION_YES,
  normalizeBooleanAnswerOptions,
} from "@/lib/question-jump-logic";
import { cn, getUserType } from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Plus,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import type * as React from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import { Card, CardContent, CardFooter } from "../ui/card";
import { FormFieldWrapper } from "../ui/form-field-wrapper";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { RichTextEditor } from "../ui/rich-text-editor";
import { Select, type SelectOption } from "../ui/select";
import { Switch } from "../ui/switch";
import { useAppForm } from "../ui/tanstack-form";
import { Textarea } from "../ui/textarea";

/** Primary footer button label for create / save / reactivate. */
export function getQuestionFormPrimaryButtonLabel(options: {
  isSubmitting: boolean;
  questionId: string | null;
  isDeactivated: boolean;
  submitAction?: "deactivate" | "reactivate" | "save" | null;
}): string {
  if (options.isSubmitting) {
    if (options.submitAction === "deactivate") {
      return "Save Changes";
    }
    if (options.isDeactivated || options.submitAction === "reactivate") {
      return "Reactivating...";
    }
    return "Saving...";
  }
  if (!options.questionId) {
    return "Create Question";
  }
  if (options.isDeactivated) {
    return "Reactivate";
  }
  return "Save Changes";
}

/** Show Deactivate only when editing an active (non-deleted) question. */
export function shouldShowQuestionDeactivateButton(options: {
  questionId: string | null;
  isDeactivated: boolean;
  isReadOnly: boolean;
}): boolean {
  return Boolean(
    options.questionId && !options.isDeactivated && !options.isReadOnly,
  );
}

export function getQuestionDeactivateButtonLabel(
  isSubmitting: boolean,
  submitAction?: "deactivate" | "reactivate" | "save" | null,
): string {
  return isSubmitting && submitAction === "deactivate"
    ? "Deactivating..."
    : "Deactivate";
}

// Type for simplified scopes used in the UI
type SimplifiedScope = "global" | "group" | "jurisdiction";

export const QUESTION_ANSWER_TYPE_OPTIONS: SelectOption[] = [
  { value: "address", label: "Address" },
  { value: "boolean", label: "Boolean" },
  { value: "company_contact", label: "Company" },
  { value: "confirmation", label: "Confirmation" },
  { value: "date", label: "Date" },
  { value: "document", label: "Document" },
  { value: "email", label: "Email" },
  { value: "information", label: "Information" },
  { value: "live_link", label: "Live Link" },
  { value: "multi_select", label: "Multi Select" },
  { value: "name_address_contact", label: "Name, Address, Contact" },
  { value: "number", label: "Number" },
  { value: "phone_number", label: "Phone Number" },
  { value: "photo", label: "Photo" },
  { value: "single_select", label: "Single Select" },
  { value: "text", label: "Text" },
  { value: "url", label: "URL" },
];

// Backend now uses scope (global|group|jurisdiction) + question_type (registration|inspection|deregistration)
function getBackendScope(simplifiedScope: SimplifiedScope): QuestionScope {
  return simplifiedScope;
}

/** Row order for answer options: prefer API `answer_option_keys_order`, else JSON key order. */
function initialAnswerOptionRowOrder(
  answerOptions: Record<string, number | null>,
  savedOrder: string[] | null | undefined,
): string[] {
  const keys = Object.keys(answerOptions);
  if (!savedOrder?.length) {
    return [...keys];
  }
  const set = new Set(keys);
  const ordered = savedOrder.filter((k) => set.has(k));
  const seen = new Set(ordered);
  const rest = keys.filter((k) => !seen.has(k));
  return [...ordered, ...rest];
}

function getSimplifiedScope(backendScope: QuestionScope): SimplifiedScope {
  return backendScope;
}

export function supportsQuestionScreenPlacement(answerType: string): boolean {
  return answerType === "information" || answerType === "confirmation";
}

function usesAnswerOptions(answerType: string): boolean {
  return (
    answerType === "boolean" ||
    answerType === "single_select" ||
    answerType === "multi_select"
  );
}

function coerceJumpTarget(value: unknown): number | null {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.trunc(parsed);
    }
  }
  return null;
}

/** Coerce stored options so leftover list/string shapes cannot fail Save. */
export function normalizeAnswerOptionsForForm(
  answerType: string,
  raw: unknown,
): Record<string, number | null> {
  if (!usesAnswerOptions(answerType)) {
    return {};
  }
  const record: Record<string, unknown> = {};
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (item == null) continue;
      record[String(item)] = null;
    }
  } else if (raw && typeof raw === "object") {
    Object.assign(record, raw);
  }
  const normalized: Record<string, number | null> = {};
  for (const [key, value] of Object.entries(record)) {
    normalized[key] = coerceJumpTarget(value);
  }
  if (answerType === "boolean") {
    return normalizeBooleanAnswerOptions(normalized);
  }
  return normalized;
}

/**
 * Drop type-irrelevant leftovers before Save validation.
 * Hidden fields (display_up_front, answer_options, stale jumps) otherwise grey
 * out Save even though the operator cannot edit them for the current type.
 */
export function sanitizeQuestionFormValuesForValidation<
  T extends Record<string, unknown>,
>(values: T): T {
  const answerType = String(values.answer_type ?? "text");
  return {
    ...values,
    display_up_front: supportsQuestionScreenPlacement(answerType)
      ? Boolean(values.display_up_front)
      : false,
    answer_options: normalizeAnswerOptionsForForm(
      answerType,
      values.answer_options,
    ),
    suggest_previous_property_managers:
      answerType === "company_contact" &&
      Boolean(values.suggest_previous_property_managers),
    copy_from_question_ids:
      answerType === "company_contact" &&
      Array.isArray(values.copy_from_question_ids)
        ? values.copy_from_question_ids
        : [],
    display_order:
      typeof values.display_order === "number" &&
      Number.isFinite(values.display_order)
        ? Math.trunc(values.display_order)
        : 0,
    jump_target: coerceJumpTarget(values.jump_target),
  };
}

// Info button component for displaying help text
function InfoButton({ children }: { children: React.ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-5 w-5 p-0 hover:bg-muted"
        >
          <HelpCircle className="h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="start">
        <div className="space-y-2 text-sm">{children}</div>
      </PopoverContent>
    </Popover>
  );
}

/** Example-only placeholders: italic so they are not mistaken for saved values. */
const EXAMPLE_PLACEHOLDER_CLASSNAME =
  "placeholder:italic placeholder:text-placeholder-foreground";

// Simplified scope - just the three main types
// The actual backend scope is determined by context (registration/inspection/deregistration panel)
const QuestionFormBaseSchema = z.object({
  scope: z.enum(["global", "group", "jurisdiction"]),
  license_type_id: z.string().uuid("Invalid license type ID"),
  // Optional/nullable so validation passes when field is hidden (e.g. editing global question)
  jurisdiction_id: z
    .string()
    .uuid("Invalid jurisdiction ID")
    .nullable()
    .optional(),
  question_group_id: z
    .string()
    .uuid("Invalid question group ID")
    .nullable()
    .optional(),
  question: z.string().min(1, "Question is required"),
  answer_type: z.enum([
    "address",
    "text",
    "boolean",
    "confirmation",
    "date",
    "number",
    "single_select",
    "multi_select",
    "information",
    "email",
    "phone_number",
    "name_address_contact",
    "company_contact",
    "document",
    "photo",
    "live_link",
    "url",
  ]),
  answer_options: z.record(z.string(), z.number().nullable()),
  display_order: z.number().int(),
  jump_target: z.number().int().nullable(),
  is_deleted: z.boolean(),
  information_text: z.string(),
  answer_required: z.boolean(),
  display_up_front: z.boolean(),
  show_on_fees_payment_screen: z.boolean(),
  select_from_fixed_list: z.boolean(),
  allow_copy_registrant_info: z.boolean(),
  suggest_previous_property_managers: z.boolean(),
  copy_from_question_ids: z.array(z.string().uuid()),
  contact_name_required: z.boolean(),
  contact_address_required: z.boolean(),
  contact_email_required: z.boolean(),
  contact_phone_required: z.boolean(),
  contact_company_name_required: z.boolean(),
  contact_require_company_or_person_name: z.boolean(),
  use_contact_label: z.boolean(),
  // live_link only; optional so non–live-link submissions validate without them.
  url_template: z.string().optional(),
  blank_answer_url: z.string().optional(),
  copy_value_to_clipboard: z.boolean().optional(),
  http_method: z.enum(["GET", "POST", "EXTENSION"]).optional(),
  value_extract_regex: z.string().optional(),
  request_fields_json: z.string().optional(),
  browser_helper_config_json: z.string().optional(),
  allow_multiple_photos: z.boolean().optional(),
  allow_multiple_urls: z.boolean().optional(),
  allow_multiple_documents: z.boolean().optional(),
  show_in_list: z.boolean(),
  contact_name_show_in_list: z.boolean(),
  contact_company_name_show_in_list: z.boolean(),
  contact_email_show_in_list: z.boolean(),
  contact_phone_show_in_list: z.boolean(),
  contact_address_show_in_list: z.boolean(),
});

const QuestionFormSchemaInner = QuestionFormBaseSchema.refine(
  (data) => {
    // jurisdiction scope requires jurisdiction_id
    if (data.scope === "jurisdiction" && !data.jurisdiction_id) {
      return false;
    }
    return true;
  },
  {
    message: "Jurisdiction ID is required for jurisdiction-specific questions",
    path: ["jurisdiction_id"],
  },
)
  .refine(
    (data) => {
      // Global questions should not have jurisdiction_id
      if (data.scope === "global" && data.jurisdiction_id) {
        return false;
      }
      return true;
    },
    {
      message: "Jurisdiction ID should not be set for global questions",
      path: ["jurisdiction_id"],
    },
  )
  .refine(
    (data) => {
      // group scope requires question_group_id
      if (data.scope === "group" && !data.question_group_id) {
        return false;
      }
      return true;
    },
    {
      message: "Question Group is required for group questions",
      path: ["question_group_id"],
    },
  )
  .refine(
    (data) => {
      // group scope should not have jurisdiction_id
      if (data.scope === "group" && data.jurisdiction_id) {
        return false;
      }
      return true;
    },
    {
      message: "Jurisdiction ID should not be set for group questions",
      path: ["jurisdiction_id"],
    },
  )
  .refine(
    (data) => !(data.display_up_front && data.show_on_fees_payment_screen),
    {
      message:
        "Display up front and show on fees payment screen cannot both be selected",
      path: ["show_on_fees_payment_screen"],
    },
  );

// Full schema with refinements (for form validation)
export const QuestionFormSchema = z.preprocess((raw) => {
  if (!raw || typeof raw !== "object") {
    return raw;
  }
  return sanitizeQuestionFormValuesForValidation(
    raw as Record<string, unknown>,
  );
}, QuestionFormSchemaInner);

/** True when form values pass schema validation (save would succeed). */
export function isQuestionFormSchemaValid(
  values: unknown,
): values is z.infer<typeof QuestionFormSchemaInner> {
  return QuestionFormSchema.safeParse(values).success;
}

/** Unique validation messages for toast/display when Save is clicked with invalid data. */
export function getQuestionFormValidationMessages(values: unknown): string[] {
  const parsed = QuestionFormSchema.safeParse(values);
  if (parsed.success) {
    return [];
  }
  return [...new Set(parsed.error.issues.map((issue) => issue.message))];
}

type QuestionTypeForGroup = "registration" | "inspection" | "deregistration";

// Question Group Field Component. When hideLabel is true, no "Question Group" label is shown and content only appears when scope is "group".
function QuestionGroupField({
  form,
  licenseTypeId,
  questionType,
  isReadOnly,
  hideLabel = false,
}: {
  form: any;
  licenseTypeId: string;
  questionType: QuestionTypeForGroup;
  isReadOnly: boolean;
  hideLabel?: boolean;
}) {
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const prevLicenseTypeIdRef = useRef<string | null>(null);
  const queryClient = useQueryClient();

  // Clear question group when license type changes so the dropdown reflects the new license type
  useEffect(() => {
    if (
      prevLicenseTypeIdRef.current !== null &&
      prevLicenseTypeIdRef.current !== licenseTypeId
    ) {
      form.setFieldValue("question_group_id", null);
    }
    prevLicenseTypeIdRef.current = licenseTypeId || null;
  }, [licenseTypeId, form]);

  // Fetch only active question groups for the current question type and license type
  const { data: groupsData } = useQuery({
    queryKey: ["questionGroups", licenseTypeId, questionType] as const,
    queryFn: async ({ queryKey }) => {
      const [, ltId, qType] = queryKey;
      if (!ltId) return [];
      const response =
        await getQuestionGroupsOperatorLicenseTypesLicenseTypeIdQuestionGroupsGet(
          {
            path: { license_type_id: ltId },
            query: { question_type: qType },
          },
        );
      const all = response.data || [];
      return all.filter((g: { deleted_at?: string | null }) => !g.deleted_at);
    },
    enabled: !!licenseTypeId,
  });

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || !licenseTypeId || isCreating) return;

    setIsCreating(true);
    try {
      const response =
        await createQuestionGroupOperatorLicenseTypesLicenseTypeIdQuestionGroupsPost(
          {
            path: { license_type_id: licenseTypeId },
            body: {
              license_type_id: licenseTypeId,
              name: newGroupName,
              question_type: questionType,
            },
          },
        );

      if (response?.data?.id) {
        form.setFieldValue("question_group_id", response.data.id);
        setNewGroupName("");
        setIsCreatingGroup(false);
        await queryClient.invalidateQueries({
          queryKey: ["questionGroups", licenseTypeId, questionType],
        });
      }
    } catch (error) {
      console.error("Failed to create question group:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const fieldContent = (field: any) => (
    <form.Subscribe selector={(state: any) => state.values.scope}>
      {(currentScope: any) => {
        const isGroupScope = currentScope === "group";

        if (!isGroupScope) return null;

        const groups = groupsData || [];
        const groupOptions: SelectOption[] = groups.map((group: any) => ({
          label: group.name,
          value: group.id,
        }));

        if (!licenseTypeId) {
          return (
            <div className="text-sm text-muted-foreground">
              Please select a license type first
            </div>
          );
        }

        return (
          <div className="space-y-2">
            {!isCreatingGroup ? (
              <div className="flex gap-2 items-start">
                <div className="min-w-0 flex-1">
                  <Select
                    options={groupOptions}
                    value={field.state.value ? [field.state.value] : []}
                    onValueChange={(values) =>
                      field.handleChange(values[0] || null)
                    }
                    placeholder="Select question group"
                    searchPlaceholder="Search groups..."
                    emptyMessage="No groups found."
                    disabled={isReadOnly}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsCreatingGroup(true)}
                  disabled={isReadOnly}
                  className="shrink-0"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create New Group
                </Button>
              </div>
            ) : (
              <div className="space-y-2 p-3 border rounded-lg bg-muted/50">
                <Label>New Group Name</Label>
                <Input
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Enter group name"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCreateGroup();
                    } else if (e.key === "Escape") {
                      setIsCreatingGroup(false);
                      setNewGroupName("");
                    }
                  }}
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCreateGroup}
                    disabled={!newGroupName.trim() || isCreating}
                    className="flex-1"
                  >
                    {isCreating ? "Creating..." : "Create"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setIsCreatingGroup(false);
                      setNewGroupName("");
                    }}
                    disabled={isCreating}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      }}
    </form.Subscribe>
  );

  return (
    <form.AppField
      name="question_group_id"
      validators={{
        onChange: QuestionFormBaseSchema.shape.question_group_id,
      }}
    >
      {(field: any) =>
        hideLabel ? (
          fieldContent(field)
        ) : (
          <FormFieldWrapper field={field} label="Question Group">
            {fieldContent(field)}
          </FormFieldWrapper>
        )
      }
    </form.AppField>
  );
}

interface QuestionFormProps {
  questionId: string | null;
  scope: SimplifiedScope | null;
  questionData: {
    scope: QuestionScope;
    license_type_id: string;
    jurisdiction_id: string | null;
    question_group_id?: string | null;
    jurisdiction_name: string | null;
    question: string;
    answer_type: LicenseQuestionAnswerType;
    answer_options: Record<string, number | null>;
    answer_option_keys_order?: string[] | null;
    display_order: number;
    jump_target?: number | null;
    is_deleted: boolean;
    information_text: string;
    answer_required: boolean;
    display_up_front: boolean;
    show_on_fees_payment_screen: boolean;
    select_from_fixed_list: boolean;
    allow_copy_registrant_info?: boolean;
    suggest_previous_property_managers?: boolean;
    copy_from_question_ids?: string[] | null;
    use_contact_label?: boolean;
    url_template?: string | null;
    blank_answer_url?: string | null;
    copy_value_to_clipboard?: boolean;
    http_method?: string | null;
    value_extract_regex?: string | null;
    request_fields?: Record<string, string> | null;
    browser_helper_config?: Record<string, unknown> | null;
    allow_multiple_photos?: boolean;
    allow_multiple_urls?: boolean;
    allow_multiple_documents?: boolean;
    show_in_list?: boolean;
    contact_name_show_in_list?: boolean;
    contact_company_name_show_in_list?: boolean;
    contact_email_show_in_list?: boolean;
    contact_phone_show_in_list?: boolean;
    contact_address_show_in_list?: boolean;
  } | null;
  /** Jurisdictions (with optional license_type_configurations for filtering by selected license type) */
  jurisdictions: Array<{
    id: string;
    name: string;
    license_type_configurations?: Array<{ license_type_id: string }>;
  }>;
  licenseTypes: {
    id: string;
    name: string;
  }[];
  restrictToInspectionScopes?: boolean;
  restrictToDeregistrationScopes?: boolean;
  userType?: UserType;
  /**
   * Dashboard jurisdiction filter ids at panel open. Used only for new-question
   * defaults (exactly one → jurisdiction scope + that id). Not re-applied mid-edit.
   */
  filterJurisdictionIds?: string[];
  /**
   * Dashboard license type filter ids at panel open. Used only for new-question
   * defaults (exactly one → pre-select that license type).
   */
  filterLicenseTypeIds?: string[];
  onSubmit: (data: z.infer<typeof QuestionFormSchema>) => Promise<void>;
  onCancel?: () => void;
}

export function QuestionForm({
  questionId,
  scope,
  questionData,
  jurisdictions,
  licenseTypes,
  restrictToInspectionScopes = false,
  restrictToDeregistrationScopes = false,
  userType,
  filterJurisdictionIds = [],
  filterLicenseTypeIds = [],
  onSubmit,
  onCancel,
}: QuestionFormProps) {
  // Government users cannot edit global questions
  const { user } = useAuth0();
  const actualUserType = userType || getUserType(user);
  const isGovernment = isGovernmentPortalViewer(actualUserType);
  const { jurisdictionId: governmentJurisdictionIdFromAuth } =
    useGovernmentPortalJurisdictionId(isGovernment);
  // Fallback: use first jurisdiction from loaded data when Auth0 claim is missing (so jurisdiction_id is always set for government add-new)
  const governmentJurisdictionId =
    governmentJurisdictionIdFromAuth ?? jurisdictions?.[0]?.id ?? null;
  const isGlobalQuestion =
    questionId && questionData && questionData.scope === "global";
  const isReadOnly: boolean = Boolean(isGovernment && isGlobalQuestion);
  const configuredQuestionType = restrictToInspectionScopes
    ? "inspection"
    : restrictToDeregistrationScopes
      ? "deregistration"
      : "registration";
  const { data: catalogQuestions } = useQuery({
    ...getAllLicenseTypeQuestionsOptions({
      query: { include_deleted: false },
    }),
  });

  // Stable order for answer options: set when panel opens (form reset), not when editing values
  const orderedAnswerOptionKeysRef = useRef<string[]>([]);
  /** When set, next submit forces is_deleted (Deactivate=true / Reactivate=false). */
  const isDeletedOverrideRef = useRef<boolean | null>(null);
  const [submitAction, setSubmitAction] = useState<
    "deactivate" | "reactivate" | "save" | null
  >(null);

  const isDeactivated = Boolean(questionData?.is_deleted);

  // Snapshot dashboard filters once per create-panel mount so mid-edit / live
  // filter changes while the sheet is open do not override user selections.
  const filterSnapshotRef = useRef({
    jurisdictions: filterJurisdictionIds,
    licenseTypes: filterLicenseTypeIds,
    fallbackScope: (scope || "global") as SimplifiedScope,
  });

  const resolveCreateDefaults = useCallback(
    () =>
      getCreateFormFilterDefaults({
        selectedJurisdictions: filterSnapshotRef.current.jurisdictions,
        selectedLicenseTypes: filterSnapshotRef.current.licenseTypes,
        fallbackScope: filterSnapshotRef.current.fallbackScope,
        forceJurisdictionScope: Boolean(isGovernment && !questionId),
        governmentJurisdictionId,
      }),
    [isGovernment, questionId, governmentJurisdictionId],
  );

  const isNewQuestion = !questionId && !questionData;
  const initialCreateDefaults = resolveCreateDefaults();

  // For government / single-jurisdiction filter creates, prefer jurisdiction scope
  const defaultScope = isNewQuestion
    ? initialCreateDefaults.scope
    : scope || "global";

  const defaultJurisdictionId = isNewQuestion
    ? initialCreateDefaults.jurisdictionId
    : (questionData?.jurisdiction_id ?? null);

  const defaultLicenseTypeId =
    (questionData?.license_type_id ||
      (isNewQuestion ? initialCreateDefaults.licenseTypeId : null) ||
      licenseTypes?.[0]?.id) ??
    "";

  const form = useAppForm({
    defaultValues: {
      scope: questionData
        ? getSimplifiedScope(questionData.scope)
        : defaultScope,
      license_type_id: defaultLicenseTypeId,
      jurisdiction_id: defaultJurisdictionId,
      question_group_id: questionData?.question_group_id || null,
      question: questionData?.question || "",
      answer_type: questionData?.answer_type || "text",
      answer_options: normalizeAnswerOptionsForForm(
        questionData?.answer_type || "text",
        questionData?.answer_options,
      ),
      display_order: questionData?.display_order || 0,
      jump_target: questionData?.jump_target ?? null,
      is_deleted: questionData?.is_deleted || false,
      information_text: questionData?.information_text || "",
      answer_required: questionData?.answer_required ?? true,
      display_up_front: supportsQuestionScreenPlacement(
        questionData?.answer_type || "text",
      )
        ? (questionData?.display_up_front ?? false)
        : false,
      show_on_fees_payment_screen:
        questionData?.show_on_fees_payment_screen ?? false,
      select_from_fixed_list: questionData?.select_from_fixed_list ?? false,
      allow_copy_registrant_info:
        questionData?.allow_copy_registrant_info ?? true,
      suggest_previous_property_managers:
        questionData?.suggest_previous_property_managers ?? false,
      copy_from_question_ids: questionData?.copy_from_question_ids ?? [],
      ...contactRequirementsFromApiQuestion(questionData ?? null),
      use_contact_label: questionData?.use_contact_label ?? false,
      url_template: questionData?.url_template ?? "",
      blank_answer_url: questionData?.blank_answer_url ?? "",
      copy_value_to_clipboard: questionData?.copy_value_to_clipboard ?? false,
      http_method:
        questionData?.http_method === "POST" ||
        questionData?.http_method === "post"
          ? "POST"
          : questionData?.http_method === "EXTENSION" ||
              questionData?.http_method === "extension"
            ? "EXTENSION"
            : "GET",
      value_extract_regex: questionData?.value_extract_regex ?? "",
      request_fields_json: questionData?.request_fields
        ? JSON.stringify(questionData.request_fields, null, 2)
        : "",
      browser_helper_config_json: questionData?.browser_helper_config
        ? JSON.stringify(questionData.browser_helper_config, null, 2)
        : "",
      allow_multiple_photos: questionData?.allow_multiple_photos ?? false,
      allow_multiple_urls: questionData?.allow_multiple_urls ?? false,
      allow_multiple_documents: questionData?.allow_multiple_documents ?? false,
      ...showInListFormValues(questionData?.answer_type, {
        show_in_list: questionData?.show_in_list,
        contact_name_show_in_list: questionData?.contact_name_show_in_list,
        contact_company_name_show_in_list:
          questionData?.contact_company_name_show_in_list,
        contact_email_show_in_list: questionData?.contact_email_show_in_list,
        contact_phone_show_in_list: questionData?.contact_phone_show_in_list,
        contact_address_show_in_list:
          questionData?.contact_address_show_in_list,
      }),
    },
    validators: {
      onSubmit: QuestionFormSchema as never,
    },
    onSubmit: async ({ value }) => {
      // Convert simplified scope to backend scope before submitting
      const backendScope = getBackendScope(value.scope);

      // Force answer_required to true for boolean and single_select
      const opts = (value.answer_options || {}) as Record<
        string,
        number | null
      >;
      const isSelect =
        value.answer_type === "single_select" ||
        value.answer_type === "multi_select";
      const isBoolean = value.answer_type === "boolean";
      const resolvedBooleanOptions = isBoolean
        ? normalizeBooleanAnswerOptions(opts)
        : opts;
      let resolvedAnswerRequired = value.answer_required;
      if (isNacStyleAnswerType(value.answer_type)) {
        resolvedAnswerRequired = deriveAnswerRequiredForContactQuestion(
          value.answer_type,
          {
            contact_name_required: value.contact_name_required,
            contact_address_required: value.contact_address_required,
            contact_email_required: value.contact_email_required,
            contact_phone_required: value.contact_phone_required,
            contact_company_name_required: value.contact_company_name_required,
            contact_require_company_or_person_name:
              value.contact_require_company_or_person_name,
          },
        );
      } else if (
        value.answer_type === "boolean" ||
        value.answer_type === "single_select"
      ) {
        resolvedAnswerRequired = true;
      }
      const deletedOverride = isDeletedOverrideRef.current;
      isDeletedOverrideRef.current = null;
      let parsedRequestFields: Record<string, string> | null = null;
      let parsedBrowserHelperConfig: Record<string, unknown> | null = null;
      if (value.answer_type === "live_link") {
        const rawFields = (value.request_fields_json ?? "").trim();
        if (rawFields) {
          try {
            const parsed = JSON.parse(rawFields) as unknown;
            if (
              !parsed ||
              typeof parsed !== "object" ||
              Array.isArray(parsed)
            ) {
              toast.error(
                "Request fields must be a JSON object of string values.",
              );
              return;
            }
            parsedRequestFields = Object.fromEntries(
              Object.entries(parsed as Record<string, unknown>).map(
                ([k, v]) => [k, v == null ? "" : String(v)],
              ),
            );
          } catch {
            toast.error("Request fields must be valid JSON.");
            return;
          }
        }
        const rawHelper = (value.browser_helper_config_json ?? "").trim();
        if (rawHelper) {
          try {
            const parsed = JSON.parse(rawHelper) as unknown;
            if (
              !parsed ||
              typeof parsed !== "object" ||
              Array.isArray(parsed)
            ) {
              toast.error("Browser helper config must be a JSON object.");
              return;
            }
            parsedBrowserHelperConfig = parsed as Record<string, unknown>;
          } catch {
            toast.error("Browser helper config must be valid JSON.");
            return;
          }
        }
      }
      const {
        request_fields_json: _requestFieldsJson,
        browser_helper_config_json: _browserHelperConfigJson,
        ...valueWithoutRequestFieldsJson
      } = value;
      const finalValue = {
        ...valueWithoutRequestFieldsJson,
        scope: backendScope as QuestionScope, // Now has backend scope
        answer_required: resolvedAnswerRequired,
        answer_options: isBoolean
          ? resolvedBooleanOptions
          : value.answer_options,
        answer_option_keys_order: isSelect
          ? orderedAnswerOptionKeysRef.current.filter((k) => k in opts)
          : isBoolean
            ? [...BOOLEAN_ANSWER_OPTION_KEYS_ORDER]
            : null,
        is_deleted:
          deletedOverride !== null ? deletedOverride : value.is_deleted,
        http_method:
          value.answer_type === "live_link"
            ? value.http_method === "POST"
              ? "POST"
              : value.http_method === "EXTENSION"
                ? "EXTENSION"
                : "GET"
            : "GET",
        value_extract_regex:
          value.answer_type === "live_link"
            ? (value.value_extract_regex ?? "").trim() || null
            : null,
        request_fields:
          value.answer_type === "live_link" ? parsedRequestFields : null,
        browser_helper_config:
          value.answer_type === "live_link" && value.http_method === "EXTENSION"
            ? parsedBrowserHelperConfig
            : null,
        url_template:
          value.answer_type === "live_link"
            ? (value.url_template ?? "").trim() || null
            : null,
        blank_answer_url:
          value.answer_type === "live_link"
            ? (value.blank_answer_url ?? "").trim() || null
            : null,
        display_up_front: supportsQuestionScreenPlacement(value.answer_type)
          ? value.display_up_front && !value.show_on_fees_payment_screen
          : false,
        show_on_fees_payment_screen:
          value.show_on_fees_payment_screen && !value.display_up_front,
      } as any; // Type with backend scope for onSubmit
      await onSubmit(finalValue);
    },
  });

  // Reset form when questionData, scope, or licenseTypes change (for duplication, add, edit, or when license types load)
  useEffect(() => {
    const isCreate = !questionId && !questionData;
    const defaults = resolveCreateDefaults();
    const resetScope = isCreate
      ? defaults.scope
      : questionData
        ? getSimplifiedScope(questionData.scope)
        : scope || "global";
    const resetJurisdictionId = isCreate
      ? defaults.jurisdictionId
      : (questionData?.jurisdiction_id ?? null);
    const defaultLicenseTypeId =
      (questionData?.license_type_id ||
        (isCreate ? defaults.licenseTypeId : null) ||
        licenseTypes?.[0]?.id) ??
      "";
    const resetValues = {
      scope: questionData ? getSimplifiedScope(questionData.scope) : resetScope,
      license_type_id: defaultLicenseTypeId,
      jurisdiction_id: resetJurisdictionId,
      question_group_id: questionData?.question_group_id || null,
      question: questionData?.question || "",
      answer_type: questionData?.answer_type || "text",
      answer_options: normalizeAnswerOptionsForForm(
        questionData?.answer_type || "text",
        questionData?.answer_options,
      ),
      display_order: questionData?.display_order || 0,
      jump_target: questionData?.jump_target ?? null,
      is_deleted: questionData?.is_deleted || false,
      information_text: questionData?.information_text || "",
      answer_required: questionData?.answer_required ?? true,
      display_up_front: supportsQuestionScreenPlacement(
        questionData?.answer_type || "text",
      )
        ? (questionData?.display_up_front ?? false)
        : false,
      show_on_fees_payment_screen:
        questionData?.show_on_fees_payment_screen ?? false,
      select_from_fixed_list: questionData?.select_from_fixed_list ?? false,
      allow_copy_registrant_info:
        questionData?.allow_copy_registrant_info ?? true,
      suggest_previous_property_managers:
        questionData?.suggest_previous_property_managers ?? false,
      copy_from_question_ids: questionData?.copy_from_question_ids ?? [],
      ...contactRequirementsFromApiQuestion(questionData ?? null),
      use_contact_label: questionData?.use_contact_label ?? false,
      url_template: questionData?.url_template ?? "",
      blank_answer_url: questionData?.blank_answer_url ?? "",
      copy_value_to_clipboard: questionData?.copy_value_to_clipboard ?? false,
      http_method:
        questionData?.http_method === "POST" ||
        questionData?.http_method === "post"
          ? "POST"
          : questionData?.http_method === "EXTENSION" ||
              questionData?.http_method === "extension"
            ? "EXTENSION"
            : "GET",
      value_extract_regex: questionData?.value_extract_regex ?? "",
      request_fields_json: questionData?.request_fields
        ? JSON.stringify(questionData.request_fields, null, 2)
        : "",
      browser_helper_config_json: questionData?.browser_helper_config
        ? JSON.stringify(questionData.browser_helper_config, null, 2)
        : "",
      allow_multiple_photos: questionData?.allow_multiple_photos ?? false,
      allow_multiple_urls: questionData?.allow_multiple_urls ?? false,
      allow_multiple_documents: questionData?.allow_multiple_documents ?? false,
      ...showInListFormValues(questionData?.answer_type, {
        show_in_list: questionData?.show_in_list,
        contact_name_show_in_list: questionData?.contact_name_show_in_list,
        contact_company_name_show_in_list:
          questionData?.contact_company_name_show_in_list,
        contact_email_show_in_list: questionData?.contact_email_show_in_list,
        contact_phone_show_in_list: questionData?.contact_phone_show_in_list,
        contact_address_show_in_list:
          questionData?.contact_address_show_in_list,
      }),
    };

    form.reset(resetValues);
    // Preserve operator-configured order (API); JSONB alone does not preserve map key order
    orderedAnswerOptionKeysRef.current =
      resetValues.answer_type === "boolean"
        ? [...BOOLEAN_ANSWER_OPTION_KEYS_ORDER]
        : initialAnswerOptionRowOrder(
            (resetValues.answer_options || {}) as Record<string, number | null>,
            questionData?.answer_option_keys_order,
          );
  }, [
    questionData,
    scope,
    licenseTypes,
    form.reset,
    questionId,
    resolveCreateDefaults,
  ]);

  // For government add-new: set hidden scope/jurisdiction/license_type before first paint so validation passes
  useLayoutEffect(() => {
    if (!isGovernment || questionId || questionData) return;
    const defaults = resolveCreateDefaults();
    form.setFieldValue("scope", defaults.scope);
    if (defaults.jurisdictionId) {
      form.setFieldValue("jurisdiction_id", defaults.jurisdictionId);
    }
    const licenseTypeId = defaults.licenseTypeId || licenseTypes?.[0]?.id || "";
    if (licenseTypeId) {
      form.setFieldValue("license_type_id", licenseTypeId);
    }
  }, [
    isGovernment,
    questionId,
    questionData,
    licenseTypes,
    resolveCreateDefaults,
    form,
  ]);

  const handleSubmit = async (
    e?: React.FormEvent,
    options?: { is_deleted?: boolean },
  ) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (options?.is_deleted === true) {
      isDeletedOverrideRef.current = true;
      setSubmitAction("deactivate");
    } else if (options?.is_deleted === false || isDeactivated) {
      isDeletedOverrideRef.current = false;
      setSubmitAction("reactivate");
    } else {
      isDeletedOverrideRef.current = null;
      setSubmitAction("save");
    }
    const validationMessages = getQuestionFormValidationMessages(
      form.state.values,
    );
    if (validationMessages.length > 0) {
      toast.error(
        validationMessages.length === 1
          ? validationMessages[0]
          : "Please fix the form before saving",
        validationMessages.length > 1
          ? { description: validationMessages.join("\n") }
          : undefined,
      );
    }
    try {
      // When invalid, handleSubmit runs validators (field errors) and skips onSubmit.
      await form.handleSubmit();
    } catch (err) {
      console.error("[QuestionForm] form.handleSubmit() threw", err);
    } finally {
      isDeletedOverrideRef.current = null;
      setSubmitAction(null);
    }
  };

  const handleDeactivate = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await handleSubmit(undefined, { is_deleted: true });
  };

  // Convert data to SelectOption format
  const licenseTypeOptions: SelectOption[] = licenseTypes.map((lt) => ({
    value: lt.id,
    label: lt.name,
  }));

  return (
    <Card>
      <CardContent>
        <form.AppForm>
          <form.Subscribe
            selector={(state) => [
              state.values.scope,
              state.values.answer_type,
              state.values.license_type_id,
            ]}
          >
            {([_scope, answerType, licenseTypeId]) => {
              // Restrict jurisdictions to those with the selected license type configured (skip filter when only one jurisdiction, e.g. government)
              const jurisdictionOptions: SelectOption[] =
                !licenseTypeId || jurisdictions.length <= 1
                  ? jurisdictions.map((j) => ({
                      value: j.id,
                      label: jurisdictionOptionLabel(j),
                    }))
                  : jurisdictions
                      .filter((j) =>
                        j.license_type_configurations?.some(
                          (c) => c.license_type_id === licenseTypeId,
                        ),
                      )
                      .map((j) => ({
                        value: j.id,
                        label: jurisdictionOptionLabel(j),
                      }));
              return (
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <form.AppField
                    name="license_type_id"
                    validators={{
                      onChange: QuestionFormBaseSchema.shape.license_type_id,
                    }}
                  >
                    {(field) => (
                      <FormFieldWrapper field={field} label="License Type">
                        <Select
                          options={licenseTypeOptions}
                          value={field.state.value ? [field.state.value] : []}
                          onValueChange={(values) =>
                            field.handleChange(values[0] || "")
                          }
                          placeholder="Select license type"
                          searchPlaceholder="Search license types..."
                          emptyMessage="No license type found."
                          disabled={isReadOnly}
                        />
                      </FormFieldWrapper>
                    )}
                  </form.AppField>
                  {!questionId && !isGovernment && (
                    <form.AppField
                      name="scope"
                      validators={{
                        onChange: QuestionFormBaseSchema.shape.scope,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper field={field} label="Scope">
                          <RadioGroup
                            value={field.state.value}
                            onValueChange={(value) => {
                              const newScope = value as SimplifiedScope;
                              field.handleChange(newScope);
                              // Auto-set jurisdiction_id when switching to jurisdiction scope for government users
                              if (
                                isGovernment &&
                                governmentJurisdictionId &&
                                newScope === "jurisdiction"
                              ) {
                                form.setFieldValue(
                                  "jurisdiction_id",
                                  governmentJurisdictionId,
                                );
                              } else if (newScope === "global") {
                                form.setFieldValue("jurisdiction_id", null);
                                form.setFieldValue("question_group_id", null);
                              } else if (newScope === "group") {
                                form.setFieldValue("jurisdiction_id", null);
                              } else {
                                form.setFieldValue("question_group_id", null);
                              }
                            }}
                            className="flex flex-col space-y-2"
                            disabled={isReadOnly}
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="global"
                                id="global"
                                disabled={isGovernment}
                              />
                              <Label htmlFor="global" className="font-normal">
                                Global
                                {isGovernment && (
                                  <span className="text-muted-foreground text-xs ml-2">
                                    (Read-only)
                                  </span>
                                )}
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="group"
                                id="group"
                                disabled={isGovernment}
                              />
                              <Label htmlFor="group" className="font-normal">
                                Group
                                {isGovernment && (
                                  <span className="text-muted-foreground text-xs ml-2">
                                    (Read-only)
                                  </span>
                                )}
                              </Label>
                            </div>
                            <form.Subscribe
                              selector={(state: any) => [
                                state.values.scope,
                                state.values.license_type_id,
                              ]}
                            >
                              {(state: any) =>
                                state[0] === "group" ? (
                                  <QuestionGroupField
                                    form={form}
                                    licenseTypeId={
                                      form.state.values.license_type_id
                                    }
                                    questionType={
                                      restrictToDeregistrationScopes
                                        ? "deregistration"
                                        : restrictToInspectionScopes
                                          ? "inspection"
                                          : "registration"
                                    }
                                    isReadOnly={isReadOnly}
                                    hideLabel
                                  />
                                ) : null
                              }
                            </form.Subscribe>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="jurisdiction"
                                id="jurisdiction"
                              />
                              <Label
                                htmlFor="jurisdiction"
                                className="font-normal"
                              >
                                Jurisdiction
                              </Label>
                            </div>
                          </RadioGroup>
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  )}
                  {/* Jurisdiction scope fields: only show for operators (government add-new uses current jurisdiction, no UI) */}
                  {!questionId && !isGovernment && (
                    <form.AppField
                      name="jurisdiction_id"
                      validators={{
                        onChange: z
                          .string()
                          .uuid("Invalid jurisdiction ID")
                          .nullable(),
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper field={field}>
                          <form.Subscribe
                            selector={(state: any) => state.values.scope}
                          >
                            {(currentScope: any) => {
                              const isJurisdictionScope =
                                currentScope === "jurisdiction";
                              if (!isJurisdictionScope) return null;
                              return (
                                <Select
                                  options={jurisdictionOptions}
                                  value={
                                    field.state.value ? [field.state.value] : []
                                  }
                                  onValueChange={(values) =>
                                    field.handleChange(values[0] || null)
                                  }
                                  placeholder="Select jurisdiction"
                                  searchPlaceholder="Search jurisdiction..."
                                  emptyMessage="No jurisdiction found."
                                  disabled={isReadOnly}
                                />
                              );
                            }}
                          </form.Subscribe>
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  )}
                  <form.AppField
                    name="question"
                    validators={{
                      onChange: QuestionFormBaseSchema.shape.question,
                    }}
                  >
                    {(field) => {
                      // Check against the original saved value, not the current form value
                      const originalQuestionText = questionData?.question || "";
                      const isAutoGenerated =
                        isAutoGeneratedQuestion(originalQuestionText);
                      return (
                        <div className="space-y-2">
                          <FormFieldWrapper field={field} label="Question">
                            <Input
                              id="question"
                              placeholder="Enter the question text here"
                              value={field.state.value}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                              disabled={isReadOnly}
                            />
                          </FormFieldWrapper>
                          {isAutoGenerated && (
                            <Alert variant="warning">
                              <AlertTriangle className="size-6" />
                              <AlertDescription>
                                This is an auto-generated question used for fee
                                calculation. Changing the question text may
                                impact fee calculations and prevent the system
                                from calculating fees correctly.
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      );
                    }}
                  </form.AppField>
                  <form.AppField
                    name="answer_type"
                    validators={{
                      onChange: QuestionFormBaseSchema.shape.answer_type,
                    }}
                  >
                    {(field) => {
                      // Check against the original saved value, not the current form value
                      const originalQuestionText = questionData?.question || "";
                      const isAutoGenerated =
                        isAutoGeneratedQuestion(originalQuestionText);
                      return (
                        <div className="space-y-2">
                          <FormFieldWrapper
                            field={field}
                            label={
                              <div className="flex items-center gap-2">
                                <span>Answer Type</span>
                                <InfoButton>
                                  <div className="font-semibold">
                                    Question Answer Types
                                  </div>
                                  <p>
                                    Select the type of input users will provide
                                    when answering this question.
                                  </p>
                                  <div className="font-semibold mt-2">
                                    Available Types:
                                  </div>
                                  <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
                                    <li>
                                      <strong>Text</strong>: Free-form text
                                      input
                                    </li>
                                    <li>
                                      <strong>Number</strong>: Numeric input
                                      only
                                    </li>
                                    <li>
                                      <strong>Boolean</strong>: Yes/No choice
                                    </li>
                                    <li>
                                      <strong>Date</strong>: Date picker
                                    </li>
                                    <li>
                                      <strong>Email</strong>: Email address with
                                      validation
                                    </li>
                                    <li>
                                      <strong>Phone Number</strong>: Phone
                                      number with formatting
                                    </li>
                                    <li>
                                      <strong>Address</strong>: Full address
                                      input with geocoding
                                    </li>
                                    <li>
                                      <strong>Name, Address, Contact</strong>:
                                      Combined fields for complete contact
                                      information
                                    </li>
                                    <li>
                                      <strong>Company</strong>: Name, company
                                      name, address, email, and phone
                                    </li>
                                    <li>
                                      <strong>Single Select</strong>: Choose one
                                      option from a list (supports conditional
                                      jump logic)
                                    </li>
                                    <li>
                                      <strong>Multi Select</strong>: Choose
                                      multiple options from a list
                                    </li>
                                    <li>
                                      <strong>Confirmation</strong>: Rich
                                      information text with a required Confirm
                                      button
                                    </li>
                                    <li>
                                      <strong>Document</strong>: File upload
                                    </li>
                                    <li>
                                      <strong>Photo</strong>: Camera capture
                                      with location, time, and timezone metadata
                                      (inspection portal); optional multiple
                                      photos
                                    </li>
                                    <li>
                                      <strong>Live Link</strong>: Single-line
                                      text answer that is displayed as a
                                      clickable link built from a URL template
                                    </li>
                                    <li>
                                      <strong>Information</strong>: Display-only
                                      text (no user input required)
                                    </li>
                                  </ul>
                                  <div className="font-semibold mt-2">
                                    Note:
                                  </div>
                                  <p className="text-muted-foreground text-sm">
                                    Only Single Select questions support
                                    conditional jump logic through answer
                                    options.
                                  </p>
                                </InfoButton>
                              </div>
                            }
                          >
                            <Select
                              options={QUESTION_ANSWER_TYPE_OPTIONS}
                              value={
                                field.state.value ? [field.state.value] : []
                              }
                              onValueChange={(values) => {
                                const next =
                                  values[0] as LicenseQuestionAnswerType;
                                field.handleChange(next);
                                if (next === "boolean") {
                                  form.setFieldValue(
                                    "answer_options",
                                    normalizeBooleanAnswerOptions(
                                      form.getFieldValue("answer_options") as
                                        | Record<string, number | null>
                                        | undefined,
                                    ),
                                  );
                                  orderedAnswerOptionKeysRef.current = [
                                    ...BOOLEAN_ANSWER_OPTION_KEYS_ORDER,
                                  ];
                                } else if (
                                  next !== "single_select" &&
                                  next !== "multi_select"
                                ) {
                                  form.setFieldValue("answer_options", {});
                                  orderedAnswerOptionKeysRef.current = [];
                                }
                                if (isNacStyleAnswerType(next)) {
                                  const ar =
                                    form.getFieldValue("answer_required") ??
                                    true;
                                  const reqs = contactRequirementsForNewNacType(
                                    next,
                                    ar,
                                  );
                                  form.setFieldValue(
                                    "contact_name_required",
                                    reqs.contact_name_required,
                                  );
                                  form.setFieldValue(
                                    "contact_address_required",
                                    reqs.contact_address_required,
                                  );
                                  form.setFieldValue(
                                    "contact_email_required",
                                    reqs.contact_email_required,
                                  );
                                  form.setFieldValue(
                                    "contact_phone_required",
                                    reqs.contact_phone_required,
                                  );
                                  form.setFieldValue(
                                    "contact_company_name_required",
                                    reqs.contact_company_name_required,
                                  );
                                  form.setFieldValue(
                                    "contact_require_company_or_person_name",
                                    reqs.contact_require_company_or_person_name,
                                  );
                                  if (next === "name_address_contact") {
                                    form.setFieldValue(
                                      "use_contact_label",
                                      false,
                                    );
                                  }
                                } else {
                                  form.setFieldValue(
                                    "contact_name_required",
                                    false,
                                  );
                                  form.setFieldValue(
                                    "contact_address_required",
                                    false,
                                  );
                                  form.setFieldValue(
                                    "contact_email_required",
                                    false,
                                  );
                                  form.setFieldValue(
                                    "contact_phone_required",
                                    false,
                                  );
                                  form.setFieldValue(
                                    "contact_company_name_required",
                                    false,
                                  );
                                  form.setFieldValue(
                                    "contact_require_company_or_person_name",
                                    false,
                                  );
                                  form.setFieldValue(
                                    "use_contact_label",
                                    false,
                                  );
                                }
                                if (!isListableQuestionAnswerType(next)) {
                                  form.setFieldValue("show_in_list", false);
                                }
                                if (next !== "company_contact") {
                                  form.setFieldValue(
                                    "contact_name_show_in_list",
                                    false,
                                  );
                                  form.setFieldValue(
                                    "contact_company_name_show_in_list",
                                    false,
                                  );
                                  form.setFieldValue(
                                    "contact_email_show_in_list",
                                    false,
                                  );
                                  form.setFieldValue(
                                    "contact_phone_show_in_list",
                                    false,
                                  );
                                  form.setFieldValue(
                                    "contact_address_show_in_list",
                                    false,
                                  );
                                }
                                if (!supportsQuestionScreenPlacement(next)) {
                                  form.setFieldValue("display_up_front", false);
                                }
                              }}
                              placeholder="Select answer type"
                              searchPlaceholder="Search answer types..."
                              emptyMessage="No answer type found."
                              disabled={isReadOnly}
                            />
                          </FormFieldWrapper>
                          {isAutoGenerated && (
                            <Alert variant="warning">
                              <AlertTriangle className="size-6" />
                              <AlertDescription>
                                This is an auto-generated question used for fee
                                calculation. Changing the answer type may impact
                                fee calculations and prevent the system from
                                calculating fees correctly.
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      );
                    }}
                  </form.AppField>
                  {(answerType === "single_select" ||
                    answerType === "multi_select") && (
                    <form.AppField
                      name="answer_options"
                      validators={{
                        onChange: QuestionFormBaseSchema.shape.answer_options,
                      }}
                    >
                      {(field) => {
                        const value = field.state.value || {};
                        // Use stable order set when panel opened; sync if ref empty but we have keys (e.g. after add)
                        if (
                          orderedAnswerOptionKeysRef.current.length === 0 &&
                          Object.keys(value).length > 0
                        ) {
                          orderedAnswerOptionKeysRef.current =
                            initialAnswerOptionRowOrder(
                              value as Record<string, number | null>,
                              questionData?.answer_option_keys_order,
                            );
                        }
                        const entries: [string, number | null][] =
                          orderedAnswerOptionKeysRef.current
                            .filter((k) => value[k] !== undefined)
                            .map((k) => [k, value[k]]);

                        const swapAnswerOptionRows = (
                          fromIndex: number,
                          toIndex: number,
                        ) => {
                          if (
                            fromIndex < 0 ||
                            toIndex < 0 ||
                            fromIndex >= entries.length ||
                            toIndex >= entries.length
                          ) {
                            return;
                          }
                          const newEntries = [...entries];
                          [newEntries[fromIndex], newEntries[toIndex]] = [
                            newEntries[toIndex],
                            newEntries[fromIndex],
                          ];
                          orderedAnswerOptionKeysRef.current = newEntries.map(
                            ([k]) => k,
                          );
                          field.handleChange(Object.fromEntries(newEntries));
                        };

                        return (
                          <FormFieldWrapper
                            field={field}
                            label={
                              <div className="flex items-center gap-2">
                                <span>Answer Options</span>
                                <InfoButton>
                                  <div className="font-semibold">
                                    Conditional Jump Logic (Answer Options)
                                  </div>
                                  <p>
                                    For single-select questions, you can
                                    configure different paths based on which
                                    option the user selects. Boolean questions
                                    use the same jump rules for Yes and No.
                                  </p>
                                  <p className="mt-2">
                                    Use the <strong>up / down</strong> arrows
                                    next to each row to set option order. The
                                    first option is the default selection for
                                    registrants.
                                  </p>
                                  <div className="font-semibold mt-2">
                                    How it works:
                                  </div>
                                  <p>
                                    Each answer option has its own "Next"
                                    controls that determine where to go if that
                                    option is selected:
                                  </p>
                                  <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                                    <li>
                                      <strong>Sequential (blank)</strong>:
                                      Proceed to the next question in display
                                      order
                                    </li>
                                    <li>
                                      <strong>Display Order (number)</strong>:
                                      Jump to a specific question
                                    </li>
                                    <li>
                                      <strong>End</strong>: Stop the
                                      questionnaire if this option is selected
                                    </li>
                                  </ul>
                                  <div className="font-semibold mt-2">
                                    Example Scenario:
                                  </div>
                                  <p className="text-sm">
                                    Question: "Do you have pets?"
                                  </p>
                                  <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
                                    <li>
                                      <strong>Yes</strong> → Sequential
                                      (continue to pet questions)
                                    </li>
                                    <li>
                                      <strong>No</strong> → Jump to display
                                      order 50 (skip all pet questions)
                                    </li>
                                  </ul>
                                  <div className="font-semibold mt-2">
                                    Priority:
                                  </div>
                                  <p className="text-muted-foreground text-sm">
                                    Answer option jumps take precedence over the
                                    question-level "Next Question" setting
                                    above. The most recently answered question's
                                    jump is used.
                                  </p>
                                </InfoButton>
                              </div>
                            }
                          >
                            <div className="space-y-2">
                              {entries.map(([key, value], index) => (
                                <div
                                  // biome-ignore lint/suspicious/noArrayIndexKey: controlled list; index keeps row identity stable when reordering with move up/down.
                                  key={index}
                                  className="flex items-center gap-2"
                                >
                                  <div className="flex flex-col gap-0.5 shrink-0">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      aria-label="Move option up"
                                      disabled={isReadOnly || index === 0}
                                      onClick={() =>
                                        swapAnswerOptionRows(index, index - 1)
                                      }
                                    >
                                      <ChevronUp className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      aria-label="Move option down"
                                      disabled={
                                        isReadOnly ||
                                        index === entries.length - 1
                                      }
                                      onClick={() =>
                                        swapAnswerOptionRows(index, index + 1)
                                      }
                                    >
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <Input
                                    value={key}
                                    onChange={(e) => {
                                      const newKey = e.target.value;
                                      const newEntries = [...entries];
                                      newEntries[index] = [newKey, value];
                                      orderedAnswerOptionKeysRef.current[
                                        index
                                      ] = newKey;
                                      field.handleChange(
                                        Object.fromEntries(newEntries),
                                      );
                                    }}
                                    placeholder={`Option ${index + 1}`}
                                    disabled={isReadOnly}
                                    className="flex-1"
                                  />
                                  {answerType === "single_select" && (
                                    <>
                                      <Label className="text-sm font-normal text-muted-foreground">
                                        Next
                                      </Label>
                                      <RadioGroup
                                        value={value === -1 ? "end" : "custom"}
                                        onValueChange={(radioValue) => {
                                          const newEntries = [...entries];
                                          if (radioValue === "end") {
                                            newEntries[index] = [key, -1];
                                          } else if (radioValue === "custom") {
                                            // Switch to custom mode with empty value
                                            if (value === -1) {
                                              newEntries[index] = [key, null];
                                            }
                                          }
                                          field.handleChange(
                                            Object.fromEntries(newEntries),
                                          );
                                        }}
                                        className="flex items-center gap-1"
                                        disabled={isReadOnly}
                                      >
                                        <div className="flex items-center gap-1">
                                          <RadioGroupItem
                                            value="custom"
                                            id={`option_${index}_custom`}
                                          />
                                          <Input
                                            type="number"
                                            placeholder="Order"
                                            value={
                                              value !== null && value !== -1
                                                ? value
                                                : ""
                                            }
                                            onChange={(e) => {
                                              const inputValue = e.target.value;
                                              const newEntries = [...entries];
                                              if (inputValue === "") {
                                                newEntries[index] = [key, null];
                                              } else {
                                                const numValue =
                                                  Number.parseFloat(inputValue);
                                                if (!Number.isNaN(numValue)) {
                                                  newEntries[index] = [
                                                    key,
                                                    Math.floor(numValue),
                                                  ];
                                                }
                                              }
                                              field.handleChange(
                                                Object.fromEntries(newEntries),
                                              );
                                            }}
                                            onFocus={() => {
                                              // When input is focused, auto-select the custom radio
                                              if (
                                                value === null ||
                                                value === -1
                                              ) {
                                                const newEntries = [...entries];
                                                newEntries[index] = [key, 0];
                                                field.handleChange(
                                                  Object.fromEntries(
                                                    newEntries,
                                                  ),
                                                );
                                              }
                                            }}
                                            disabled={isReadOnly}
                                            className="w-20"
                                          />
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <RadioGroupItem
                                            value="end"
                                            id={`option_${index}_end`}
                                          />
                                          <Label
                                            htmlFor={`option_${index}_end`}
                                            className="text-xs font-normal"
                                          >
                                            End
                                          </Label>
                                        </div>
                                      </RadioGroup>
                                    </>
                                  )}
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const newEntries = entries.filter(
                                        (_, i) => i !== index,
                                      );
                                      orderedAnswerOptionKeysRef.current =
                                        orderedAnswerOptionKeysRef.current.filter(
                                          (_, i) => i !== index,
                                        );
                                      field.handleChange(
                                        newEntries.length > 0
                                          ? Object.fromEntries(newEntries)
                                          : {},
                                      );
                                    }}
                                    disabled={isReadOnly}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  orderedAnswerOptionKeysRef.current = [
                                    ...orderedAnswerOptionKeysRef.current,
                                    "",
                                  ];
                                  const newEntries = [...entries, ["", null]];
                                  field.handleChange(
                                    Object.fromEntries(newEntries),
                                  );
                                }}
                                className="w-full"
                                disabled={isReadOnly}
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Option
                              </Button>
                            </div>
                          </FormFieldWrapper>
                        );
                      }}
                    </form.AppField>
                  )}
                  {(answerType === "information" ||
                    answerType === "confirmation") && (
                    <form.AppField name="information_text">
                      {(field) => (
                        <FormFieldWrapper
                          field={field}
                          label="Information Text (Rich)"
                        >
                          <div
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                              }
                            }}
                          >
                            <RichTextEditor
                              value={field.state.value || ""}
                              onChange={field.handleChange}
                              onBlur={field.handleBlur}
                              readOnly={isReadOnly}
                            />
                          </div>
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  )}
                  {answerType === "boolean" && (
                    <form.AppField
                      name="answer_options"
                      validators={{
                        onChange: QuestionFormBaseSchema.shape.answer_options,
                      }}
                    >
                      {(field) => {
                        const value = normalizeBooleanAnswerOptions(
                          (field.state.value || {}) as Record<
                            string,
                            number | null
                          >,
                        );
                        const booleanRows: [string, number | null][] = [
                          [BOOLEAN_ANSWER_OPTION_YES, value.Yes],
                          [BOOLEAN_ANSWER_OPTION_NO, value.No],
                        ];

                        const setBooleanJump = (
                          key: string,
                          jump: number | null,
                        ) => {
                          field.handleChange({
                            ...value,
                            [key]: jump,
                          });
                        };

                        return (
                          <FormFieldWrapper
                            field={field}
                            label={
                              <div className="flex items-center gap-2">
                                <span>Next Question by Answer</span>
                                <InfoButton>
                                  <div className="font-semibold">
                                    Conditional Jump Logic (Yes / No)
                                  </div>
                                  <p>
                                    Configure different paths based on whether
                                    the user selects Yes or No. Uses the same
                                    jump rules as single-select answer options.
                                  </p>
                                  <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                                    <li>
                                      <strong>Sequential (blank)</strong>:
                                      Proceed to the next question in display
                                      order
                                    </li>
                                    <li>
                                      <strong>Display Order (number)</strong>:
                                      Jump to a specific question
                                    </li>
                                    <li>
                                      <strong>End</strong>: Stop the
                                      questionnaire for this answer
                                    </li>
                                  </ul>
                                </InfoButton>
                              </div>
                            }
                          >
                            <div className="space-y-2">
                              {booleanRows.map(([key, jumpValue]) => (
                                <div
                                  key={key}
                                  className="flex items-center gap-2"
                                >
                                  <span className="w-12 text-sm font-medium">
                                    {key}
                                  </span>
                                  <Label className="text-sm font-normal text-muted-foreground">
                                    Next
                                  </Label>
                                  <RadioGroup
                                    value={jumpValue === -1 ? "end" : "custom"}
                                    onValueChange={(radioValue) => {
                                      if (radioValue === "end") {
                                        setBooleanJump(key, -1);
                                      } else if (
                                        radioValue === "custom" &&
                                        jumpValue === -1
                                      ) {
                                        setBooleanJump(key, null);
                                      }
                                    }}
                                    className="flex items-center gap-1"
                                    disabled={isReadOnly}
                                  >
                                    <div className="flex items-center gap-1">
                                      <RadioGroupItem
                                        value="custom"
                                        id={`boolean_${key}_custom`}
                                      />
                                      <Input
                                        type="number"
                                        placeholder="Order"
                                        value={
                                          jumpValue !== null && jumpValue !== -1
                                            ? jumpValue
                                            : ""
                                        }
                                        onChange={(e) => {
                                          const inputValue = e.target.value;
                                          if (inputValue === "") {
                                            setBooleanJump(key, null);
                                          } else {
                                            const numValue =
                                              Number.parseFloat(inputValue);
                                            if (!Number.isNaN(numValue)) {
                                              setBooleanJump(
                                                key,
                                                Math.floor(numValue),
                                              );
                                            }
                                          }
                                        }}
                                        onFocus={() => {
                                          if (
                                            jumpValue === null ||
                                            jumpValue === -1
                                          ) {
                                            setBooleanJump(key, 0);
                                          }
                                        }}
                                        disabled={isReadOnly}
                                        className="w-20"
                                      />
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <RadioGroupItem
                                        value="end"
                                        id={`boolean_${key}_end`}
                                      />
                                      <Label
                                        htmlFor={`boolean_${key}_end`}
                                        className="text-xs font-normal"
                                      >
                                        End
                                      </Label>
                                    </div>
                                  </RadioGroup>
                                </div>
                              ))}
                            </div>
                          </FormFieldWrapper>
                        );
                      }}
                    </form.AppField>
                  )}
                  {answerType === "live_link" && (
                    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                      <form.AppField name="url_template">
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label={
                              <div className="flex items-center gap-2">
                                <span>URL Template</span>
                                <InfoButton>
                                  <div className="font-semibold">
                                    Live Link URL Template
                                  </div>
                                  <p>
                                    The registrant answers with a single line of
                                    text. When their answer is displayed it
                                    becomes a link to this URL, with{" "}
                                    <code>{"{{value}}"}</code> replaced by the
                                    value they entered. Capture groups from the
                                    extract regex are available as{" "}
                                    <code>{"{{1}}"}</code>,{" "}
                                    <code>{"{{2}}"}</code>, etc.
                                  </p>
                                  <p className="mt-2">
                                    Example (GET):{" "}
                                    <code>
                                      https://clerk-records.com/{"{{value}}"}
                                    </code>
                                  </p>
                                  <p className="mt-2">
                                    Example (POST encrypt):{" "}
                                    <code>
                                      https://www2.miamidadeclerk.gov/ocs/api/CaseInfo/encrypt/
                                      {"{{1}}"}-{"{{2}}"}-{"{{3}}"}-{"{{4}}"}
                                    </code>
                                  </p>
                                </InfoButton>
                              </div>
                            }
                          >
                            <Input
                              id="url_template"
                              type="text"
                              placeholder="Example: https://clerk-records.com/{{value}}"
                              className={EXAMPLE_PLACEHOLDER_CLASSNAME}
                              value={field.state.value ?? ""}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                              disabled={isReadOnly}
                            />
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                      <form.AppField name="blank_answer_url">
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label={
                              <div className="flex items-center gap-2">
                                <span>Blank Answer URL</span>
                                <InfoButton>
                                  <div className="font-semibold">
                                    Court records page
                                  </div>
                                  <p>
                                    Page to open when this answer is blank. The
                                    title request Cases tile uses it as the
                                    jurisdiction court records link, and no case
                                    number is inserted. Leave it empty to use
                                    the URL template when that template does not
                                    need a case number.
                                  </p>
                                </InfoButton>
                              </div>
                            }
                          >
                            <Input
                              id="blank_answer_url"
                              type="text"
                              placeholder="Example: https://clerk-records.com/search"
                              className={EXAMPLE_PLACEHOLDER_CLASSNAME}
                              value={field.state.value ?? ""}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                              disabled={isReadOnly}
                            />
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                      <form.AppField name="http_method">
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label={
                              <div className="flex items-center gap-2">
                                <span>HTTP Method</span>
                                <InfoButton>
                                  <div className="font-semibold">
                                    Live Link Method
                                  </div>
                                  <p>
                                    <strong>GET</strong> opens the resolved URL
                                    directly. <strong>POST</strong> fetches the
                                    URL (for example a clerk encrypt endpoint),
                                    reads the returned <code>qs</code> token,
                                    then opens the search-results page.{" "}
                                    <strong>EXTENSION</strong> opens the court
                                    site with a payload for the Hera Court Link
                                    browser helper, which writes sessionStorage
                                    and navigates to results.
                                  </p>
                                </InfoButton>
                              </div>
                            }
                          >
                            <RadioGroup
                              value={field.state.value ?? "GET"}
                              onValueChange={(v) =>
                                field.handleChange(
                                  v as "GET" | "POST" | "EXTENSION",
                                )
                              }
                              className="flex flex-row flex-wrap gap-4"
                              disabled={isReadOnly}
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="GET"
                                  id="http_method_get"
                                />
                                <Label htmlFor="http_method_get">GET</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="POST"
                                  id="http_method_post"
                                />
                                <Label htmlFor="http_method_post">POST</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="EXTENSION"
                                  id="http_method_extension"
                                />
                                <Label htmlFor="http_method_extension">
                                  EXTENSION
                                </Label>
                              </div>
                            </RadioGroup>
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                      <form.AppField name="value_extract_regex">
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label={
                              <div className="flex items-center gap-2">
                                <span>Value Extract Regex</span>
                                <InfoButton>
                                  <div className="font-semibold">
                                    Case Number Split
                                  </div>
                                  <p>
                                    Optional regex applied to the answer before
                                    building the URL. Capture groups become{" "}
                                    <code>{"{{1}}"}</code>,{" "}
                                    <code>{"{{2}}"}</code>, … placeholders.
                                  </p>
                                  <p className="mt-2">
                                    Miami-Dade example for{" "}
                                    <code>2026014254CA01</code>:{" "}
                                    <code>
                                      {
                                        "^([0-9]{4})([0-9]{6})([A-Z]{2})([0-9]{2})$"
                                      }
                                    </code>
                                  </p>
                                  <p className="mt-2">
                                    Hillsborough compact numbers such as{" "}
                                    <code>2026CA007806</code> are handled by the
                                    HOVER preset.
                                  </p>
                                </InfoButton>
                              </div>
                            }
                          >
                            <Input
                              id="value_extract_regex"
                              type="text"
                              placeholder="Example: ^([0-9]{4})([0-9]{6})([A-Z]{2})([0-9]{2})$"
                              className={EXAMPLE_PLACEHOLDER_CLASSNAME}
                              value={field.state.value ?? ""}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                              disabled={isReadOnly}
                            />
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                      <form.AppField name="request_fields_json">
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label={
                              <div className="flex items-center gap-2">
                                <span>Request Fields (JSON)</span>
                                <InfoButton>
                                  <div className="font-semibold">
                                    Extra Placeholders
                                  </div>
                                  <p>
                                    Optional JSON object of extra named
                                    placeholders for the URL template, e.g.{" "}
                                    <code>{`{"court":"01"}`}</code>. Values may
                                    also reference <code>{"{{value}}"}</code> or
                                    capture groups.
                                  </p>
                                </InfoButton>
                              </div>
                            }
                          >
                            <Textarea
                              id="request_fields_json"
                              placeholder='Example: {"court":"01"}'
                              className={EXAMPLE_PLACEHOLDER_CLASSNAME}
                              value={field.state.value ?? ""}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              onBlur={field.handleBlur}
                              disabled={isReadOnly}
                              rows={3}
                            />
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                      <form.AppField name="browser_helper_config_json">
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label={
                              <div className="flex items-center gap-2">
                                <span>Browser Helper Config (EXTENSION)</span>
                                <InfoButton>
                                  <div className="font-semibold">
                                    Browser Helper Config
                                  </div>
                                  <p>
                                    Used when HTTP Method is EXTENSION. Defines
                                    which <code>sessionStorage</code> keys to
                                    write on the court site and where to
                                    navigate after inject. Placeholders like{" "}
                                    <code>{"{{value}}"}</code>,{" "}
                                    <code>{"{{1}}"}</code>, and{" "}
                                    <code>{"{{3|pad:6}}"}</code> work inside
                                    template strings.
                                  </p>
                                  <p className="mt-2">
                                    Install once from{" "}
                                    <a
                                      href={getBrowserExtensionsSetupHref(
                                        actualUserType ?? UserType.OPERATOR,
                                      )}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="underline"
                                    >
                                      Setup → Extensions
                                    </a>
                                    .
                                  </p>
                                </InfoButton>
                              </div>
                            }
                          >
                            <div className="space-y-2">
                              <div className="flex flex-wrap gap-2">
                                {(
                                  [
                                    [
                                      "Hillsborough HOVER",
                                      HILLSBOROUGH_HOVER_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Broward Web2",
                                      BROWARD_WEB2_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Lee Matrix",
                                      LEE_MATRIX_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Orange FL myeclerk",
                                      ORANGE_FL_MYECLERK_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Clay Benchmark",
                                      CLAY_BENCHMARK_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Martin Benchmark",
                                      MARTIN_BENCHMARK_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "St. Johns Benchmark",
                                      ST_JOHNS_BENCHMARK_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Alachua LINDAS",
                                      ALACHUA_LINDAS_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Bradford Civitek",
                                      BRADFORD_CIVITEK_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Brevard BECA",
                                      BREVARD_BECA_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Citrus SCORSS",
                                      CITRUS_SCORSS_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Duval CORE",
                                      DUVAL_CORE_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Volusia CCMS",
                                      VOLUSIA_CCMS_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Cook Civil Case Search",
                                      COOK_CIVIL_CASE_SEARCH_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "NJ Civil Search",
                                      NJ_CIVIL_SEARCH_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Allegheny DCR",
                                      ALLEGHENY_DCR_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Peoria Tyler",
                                      PEORIA_TYLER_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Tazewell Tyler",
                                      TAZEWELL_TYLER_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "NY NYSCEF",
                                      NY_FCAS_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Judici IL",
                                      ADAMS_JUDICI_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "DuPage IL",
                                      DUPAGE_CASE_SEARCH_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Northampton PA",
                                      NORTHAMPTON_COUNTYSUITE_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "York PA",
                                      YORK_COUNTYSUITE_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Delaware PA",
                                      DELAWARE_PA_CTRACK_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Dauphin PA",
                                      DAUPHIN_COUNTYSUITE_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Erie PA",
                                      ERIE_PA_COURTPRO_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Bucks PA",
                                      BUCKS_PA_COURTPRO_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Montgomery PA",
                                      MONTGOMERY_PA_COURTPRO_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Westmoreland PA",
                                      WESTMORELAND_PA_IJEMS_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Luzerne PA",
                                      LUZERNE_PA_SEARCHIQS_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Hamilton OH",
                                      HAMILTON_OH_CASE_SEARCH_BROWSER_HELPER_PRESET,
                                    ],
                                    [
                                      "Clark OH",
                                      CLARK_OH_COURTVIEW_BROWSER_HELPER_PRESET,
                                    ],
                                  ] as const
                                ).map(([label, preset]) => (
                                  <Button
                                    key={label}
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={isReadOnly}
                                    onClick={() => {
                                      form.setFieldValue(
                                        "http_method",
                                        "EXTENSION",
                                      );
                                      form.setFieldValue(
                                        "url_template",
                                        preset.url_template,
                                      );
                                      form.setFieldValue(
                                        "value_extract_regex",
                                        preset.value_extract_regex,
                                      );
                                      form.setFieldValue(
                                        "browser_helper_config_json",
                                        JSON.stringify(
                                          preset.browser_helper_config,
                                          null,
                                          2,
                                        ),
                                      );
                                      toast.success(
                                        `Applied ${label} EXTENSION preset`,
                                      );
                                    }}
                                  >
                                    Apply {label} preset
                                  </Button>
                                ))}
                              </div>
                              <Textarea
                                id="browser_helper_config_json"
                                placeholder={`Example:\n{\n  "navigate_url_template": "https://court.example/searchResults.html",\n  "session_storage": [\n    {\n      "key": "SearchSecurityModel",\n      "template": { "CaseNumber": "{{value}}" }\n    }\n  ]\n}`}
                                value={field.state.value ?? ""}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                onBlur={field.handleBlur}
                                disabled={isReadOnly}
                                rows={10}
                                className={cn(
                                  "font-mono text-xs",
                                  EXAMPLE_PLACEHOLDER_CLASSNAME,
                                )}
                              />
                            </div>
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                      <form.AppField name="copy_value_to_clipboard">
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Copy value to clipboard"
                          >
                            <div className="flex items-center space-x-2">
                              <Label className="text-sm">NO</Label>
                              <Switch
                                id="copy_value_to_clipboard"
                                checked={field.state.value}
                                onCheckedChange={field.handleChange}
                                disabled={isReadOnly}
                              />
                              <Label className="text-sm">YES</Label>
                              <Label
                                htmlFor="copy_value_to_clipboard"
                                className="text-sm text-muted-foreground ml-2"
                              >
                                Copy the entered value to the clipboard when the
                                link is clicked
                              </Label>
                            </div>
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                    </div>
                  )}
                  {answerType === "url" && (
                    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                      <form.AppField name="allow_multiple_urls">
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Allow multiple URLs"
                          >
                            <div className="flex items-center space-x-2">
                              <Label className="text-sm">NO</Label>
                              <Switch
                                id="allow_multiple_urls"
                                checked={field.state.value}
                                onCheckedChange={field.handleChange}
                                disabled={isReadOnly}
                              />
                              <Label className="text-sm">YES</Label>
                              <Label
                                htmlFor="allow_multiple_urls"
                                className="text-sm text-muted-foreground ml-2"
                              >
                                Let the registrant enter more than one URL.
                                Answer Required means at least one URL.
                              </Label>
                            </div>
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                    </div>
                  )}
                  {answerType === "document" && (
                    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                      <form.AppField name="allow_multiple_documents">
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Allow multiple files"
                          >
                            <div className="flex items-center space-x-2">
                              <Label className="text-sm">NO</Label>
                              <Switch
                                id="allow_multiple_documents"
                                checked={field.state.value}
                                onCheckedChange={field.handleChange}
                                disabled={isReadOnly}
                              />
                              <Label className="text-sm">YES</Label>
                              <Label
                                htmlFor="allow_multiple_documents"
                                className="text-sm text-muted-foreground ml-2"
                              >
                                Let the registrant upload more than one file.
                                Each file can be a document or a photo. Answer
                                Required means at least one file.
                              </Label>
                            </div>
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                    </div>
                  )}
                  {answerType === "photo" && (
                    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                      <form.AppField name="allow_multiple_photos">
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Allow multiple photos"
                          >
                            <div className="flex items-center space-x-2">
                              <Label className="text-sm">NO</Label>
                              <Switch
                                id="allow_multiple_photos"
                                checked={field.state.value}
                                onCheckedChange={field.handleChange}
                                disabled={isReadOnly}
                              />
                              <Label className="text-sm">YES</Label>
                              <Label
                                htmlFor="allow_multiple_photos"
                                className="text-sm text-muted-foreground ml-2"
                              >
                                Let the inspector capture more than one photo
                                for this question
                              </Label>
                            </div>
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                    </div>
                  )}
                  {isNacStyleAnswerType(answerType) && (
                    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-medium">
                            Field requirements
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Choose which parts of the{" "}
                            {answerType === "company_contact"
                              ? "company"
                              : "name, address, contact"}{" "}
                            answer are mandatory. The legacy &quot;answer
                            required&quot; flag is derived automatically.
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2 shrink-0">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isReadOnly}
                            onClick={() => {
                              const r = contactRequirementsForNewNacType(
                                answerType as LicenseQuestionAnswerType,
                                true,
                              );
                              form.setFieldValue(
                                "contact_name_required",
                                r.contact_name_required,
                              );
                              form.setFieldValue(
                                "contact_address_required",
                                r.contact_address_required,
                              );
                              form.setFieldValue(
                                "contact_email_required",
                                r.contact_email_required,
                              );
                              form.setFieldValue(
                                "contact_phone_required",
                                r.contact_phone_required,
                              );
                              form.setFieldValue(
                                "contact_company_name_required",
                                r.contact_company_name_required,
                              );
                              form.setFieldValue(
                                "contact_require_company_or_person_name",
                                r.contact_require_company_or_person_name,
                              );
                            }}
                          >
                            Require all
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isReadOnly}
                            onClick={() => {
                              const r = contactRequirementsForNewNacType(
                                answerType as LicenseQuestionAnswerType,
                                false,
                              );
                              form.setFieldValue(
                                "contact_name_required",
                                r.contact_name_required,
                              );
                              form.setFieldValue(
                                "contact_address_required",
                                r.contact_address_required,
                              );
                              form.setFieldValue(
                                "contact_email_required",
                                r.contact_email_required,
                              );
                              form.setFieldValue(
                                "contact_phone_required",
                                r.contact_phone_required,
                              );
                              form.setFieldValue(
                                "contact_company_name_required",
                                r.contact_company_name_required,
                              );
                              form.setFieldValue(
                                "contact_require_company_or_person_name",
                                r.contact_require_company_or_person_name,
                              );
                            }}
                          >
                            Require none
                          </Button>
                        </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {(
                          [
                            ["contact_name_required", "Person name"] as const,
                            [
                              "contact_address_required",
                              "Address (street, city, state, ZIP)",
                            ] as const,
                            ["contact_email_required", "Email"] as const,
                            ["contact_phone_required", "Phone"] as const,
                          ] as const
                        ).map(([name, label]) => (
                          <form.AppField key={name} name={name}>
                            {(field) => (
                              <FormFieldWrapper field={field} label={label}>
                                <div className="flex items-center space-x-2">
                                  <Label className="text-sm">Optional</Label>
                                  <Switch
                                    checked={field.state.value}
                                    onCheckedChange={field.handleChange}
                                    disabled={isReadOnly}
                                  />
                                  <Label className="text-sm">Required</Label>
                                </div>
                              </FormFieldWrapper>
                            )}
                          </form.AppField>
                        ))}
                      </div>
                      {answerType === "company_contact" && (
                        <div className="space-y-4 border-t border-border pt-4">
                          <form.AppField name="contact_company_name_required">
                            {(field) => (
                              <FormFieldWrapper
                                field={field}
                                label="Company name"
                              >
                                <div className="flex items-center space-x-2">
                                  <Label className="text-sm">Optional</Label>
                                  <Switch
                                    checked={field.state.value}
                                    onCheckedChange={field.handleChange}
                                    disabled={isReadOnly}
                                  />
                                  <Label className="text-sm">Required</Label>
                                </div>
                              </FormFieldWrapper>
                            )}
                          </form.AppField>
                          <form.AppField name="contact_require_company_or_person_name">
                            {(field) => (
                              <FormFieldWrapper
                                field={field}
                                label="Either company or person name"
                              >
                                <div className="flex flex-col gap-2">
                                  <div className="flex items-center space-x-2">
                                    <Label className="text-sm">Off</Label>
                                    <Switch
                                      checked={field.state.value}
                                      onCheckedChange={field.handleChange}
                                      disabled={isReadOnly}
                                    />
                                    <Label className="text-sm">On</Label>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    When on, at least one of company name or
                                    person name must be provided (enforced when
                                    applicants answer this question).
                                  </p>
                                </div>
                              </FormFieldWrapper>
                            )}
                          </form.AppField>
                          <form.AppField name="use_contact_label">
                            {(field) => (
                              <FormFieldWrapper
                                field={field}
                                label={
                                  <span className="inline-flex items-center gap-2">
                                    Use Contact label
                                    <InfoButton>
                                      <p>
                                        When enabled, the person field is
                                        labeled <strong>Contact</strong> instead
                                        of <strong>Name</strong>, and it appears{" "}
                                        <strong>after</strong> the company name
                                        field on application forms and on
                                        license detail tiles.
                                      </p>
                                    </InfoButton>
                                  </span>
                                }
                              >
                                <div className="flex items-center space-x-2">
                                  <Label className="text-sm">Off</Label>
                                  <Switch
                                    checked={field.state.value}
                                    onCheckedChange={field.handleChange}
                                    disabled={isReadOnly}
                                  />
                                  <Label className="text-sm">On</Label>
                                </div>
                              </FormFieldWrapper>
                            )}
                          </form.AppField>
                          <form.AppField name="suggest_previous_property_managers">
                            {(field) => (
                              <FormFieldWrapper
                                field={field}
                                label="Suggest previously entered values"
                              >
                                <div className="flex flex-col gap-2">
                                  <div className="flex items-center space-x-2">
                                    <Label className="text-sm">NO</Label>
                                    <Switch
                                      id="suggest_previous_property_managers"
                                      checked={field.state.value}
                                      onCheckedChange={(checked) => {
                                        field.handleChange(checked);
                                        if (checked) {
                                          form.setFieldValue(
                                            "select_from_fixed_list",
                                            false,
                                          );
                                        }
                                      }}
                                      disabled={isReadOnly}
                                    />
                                    <Label className="text-sm">YES</Label>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Let the registrant, and operators or
                                    government users editing that registrant,
                                    choose a property manager already used on
                                    another property in the same account.
                                    Primary and secondary users share the list.
                                  </p>
                                </div>
                              </FormFieldWrapper>
                            )}
                          </form.AppField>
                          <form.AppField name="copy_from_question_ids">
                            {(field) => {
                              const selected = new Set(
                                (field.state.value as string[]) ?? [],
                              );
                              const choices = (
                                catalogQuestions?.questions ?? []
                              ).filter(
                                (candidate) =>
                                  candidate.answer_type === "company_contact" &&
                                  candidate.question_type ===
                                    configuredQuestionType &&
                                  candidate.license_type_id === licenseTypeId &&
                                  candidate.id !== questionId &&
                                  !candidate.is_deleted,
                              );
                              return (
                                <FormFieldWrapper
                                  field={field}
                                  label="Allow copy from"
                                >
                                  <div className="space-y-2">
                                    <p className="text-xs text-muted-foreground">
                                      Other company questions on this license
                                      type. A selected question is offered only
                                      when it applies to the registration being
                                      filled.
                                    </p>
                                    {choices.length === 0 ? (
                                      <p className="text-sm text-muted-foreground">
                                        No other company questions for this
                                        license type.
                                      </p>
                                    ) : (
                                      choices.map((candidate) => {
                                        const checkboxId = `copy-from-${candidate.id}`;
                                        return (
                                          <div
                                            key={candidate.id}
                                            className="flex items-start gap-2"
                                          >
                                            <input
                                              id={checkboxId}
                                              type="checkbox"
                                              className="mt-1"
                                              checked={selected.has(
                                                candidate.id,
                                              )}
                                              disabled={isReadOnly}
                                              onChange={(event) => {
                                                const next = new Set(selected);
                                                if (event.target.checked) {
                                                  next.add(candidate.id);
                                                } else {
                                                  next.delete(candidate.id);
                                                }
                                                field.handleChange([...next]);
                                              }}
                                            />
                                            <Label
                                              htmlFor={checkboxId}
                                              className="text-sm font-normal"
                                            >
                                              {candidate.question}
                                              {candidate.jurisdiction_name
                                                ? ` (${candidate.jurisdiction_name})`
                                                : candidate.question_group_name
                                                  ? ` (${candidate.question_group_name})`
                                                  : candidate.scope === "global"
                                                    ? " (Global)"
                                                    : ""}
                                            </Label>
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>
                                </FormFieldWrapper>
                              );
                            }}
                          </form.AppField>
                        </div>
                      )}
                    </div>
                  )}
                  {answerType !== "information" &&
                    answerType !== "confirmation" &&
                    answerType !== "boolean" &&
                    answerType !== "single_select" &&
                    !isNacStyleAnswerType(answerType) && (
                      <form.AppField
                        name="answer_required"
                        validators={{
                          onChange:
                            QuestionFormBaseSchema.shape.answer_required,
                        }}
                      >
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Answer Required"
                          >
                            <div className="flex items-center space-x-2">
                              <Label className="text-sm">NO</Label>
                              <Switch
                                id="answer_required"
                                checked={field.state.value}
                                onCheckedChange={field.handleChange}
                                disabled={isReadOnly}
                              />
                              <Label className="text-sm">YES</Label>
                            </div>
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                    )}

                  {/* Display up front — information and confirmation only */}
                  {supportsQuestionScreenPlacement(answerType) && (
                    <form.AppField
                      name="display_up_front"
                      validators={{
                        onChange: QuestionFormBaseSchema.shape.display_up_front,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper
                          field={field}
                          label="Display Up Front"
                        >
                          <div className="flex items-center space-x-2">
                            <Label className="text-sm">NO</Label>
                            <Switch
                              id="display_up_front"
                              checked={field.state.value}
                              onCheckedChange={(checked) => {
                                field.handleChange(checked);
                                if (checked) {
                                  form.setFieldValue(
                                    "show_on_fees_payment_screen",
                                    false,
                                  );
                                }
                              }}
                              disabled={isReadOnly}
                            />
                            <Label className="text-sm">YES</Label>
                            <Label
                              htmlFor="display_up_front"
                              className="text-sm text-muted-foreground ml-2"
                            >
                              Display this question on the address confirmation
                              step
                            </Label>
                          </div>
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  )}

                  {/* Fees payment screen — allowed on any answer type */}
                  <form.AppField
                    name="show_on_fees_payment_screen"
                    validators={{
                      onChange:
                        QuestionFormBaseSchema.shape
                          .show_on_fees_payment_screen,
                    }}
                  >
                    {(field) => (
                      <FormFieldWrapper
                        field={field}
                        label="Show on fees payment screen"
                      >
                        <div className="flex items-center space-x-2">
                          <Label className="text-sm">NO</Label>
                          <Switch
                            id="show_on_fees_payment_screen"
                            checked={field.state.value}
                            onCheckedChange={(checked) => {
                              field.handleChange(checked);
                              if (checked) {
                                form.setFieldValue("display_up_front", false);
                              }
                            }}
                            disabled={isReadOnly}
                          />
                          <Label className="text-sm">YES</Label>
                          <Label
                            htmlFor="show_on_fees_payment_screen"
                            className="text-sm text-muted-foreground ml-2"
                          >
                            Show this question on the fees / payment step when
                            collecting payment
                          </Label>
                        </div>
                      </FormFieldWrapper>
                    )}
                  </form.AppField>

                  {/* Select From Fixed List - only for name/address/contact questions */}
                  {isNacStyleAnswerType(form.state.values.answer_type) && (
                    <>
                      <form.AppField
                        name="select_from_fixed_list"
                        validators={{
                          onChange:
                            QuestionFormBaseSchema.shape.select_from_fixed_list,
                        }}
                      >
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Select From Fixed List"
                          >
                            <div className="flex items-center space-x-2">
                              <Label className="text-sm">NO</Label>
                              <Switch
                                id="select_from_fixed_list"
                                checked={field.state.value}
                                onCheckedChange={(checked) => {
                                  field.handleChange(checked);
                                  if (checked) {
                                    form.setFieldValue(
                                      "suggest_previous_property_managers",
                                      false,
                                    );
                                  }
                                }}
                                disabled={isReadOnly}
                              />
                              <Label className="text-sm">YES</Label>
                              <Label
                                htmlFor="select_from_fixed_list"
                                className="text-sm text-muted-foreground ml-2"
                              >
                                Registrant selects from a fixed list of
                                name/address/contact options instead of entering
                                free-form
                              </Label>
                            </div>
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                      <form.AppField
                        name="allow_copy_registrant_info"
                        validators={{
                          onChange:
                            QuestionFormBaseSchema.shape
                              .allow_copy_registrant_info,
                        }}
                      >
                        {(field) => (
                          <FormFieldWrapper
                            field={field}
                            label="Allow copying registrant information"
                          >
                            <div className="flex items-center space-x-2">
                              <Label className="text-sm">NO</Label>
                              <Switch
                                id="allow_copy_registrant_info"
                                checked={field.state.value}
                                onCheckedChange={field.handleChange}
                                disabled={isReadOnly}
                              />
                              <Label className="text-sm">YES</Label>
                              <Label
                                htmlFor="allow_copy_registrant_info"
                                className="text-sm text-muted-foreground ml-2"
                              >
                                Show &quot;I am the [contact]&quot; and
                                &quot;[X] is the [contact]&quot; options
                              </Label>
                            </div>
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                    </>
                  )}

                  <form.AppField
                    name="display_order"
                    validators={{
                      onChange: QuestionFormBaseSchema.shape.display_order,
                    }}
                  >
                    {(field) => (
                      <FormFieldWrapper
                        field={field}
                        label={
                          <div className="flex items-center gap-2">
                            <span>Display Order</span>
                            <InfoButton>
                              <div className="font-semibold">
                                Question Display Order
                              </div>
                              <p>
                                Questions are displayed to users in ascending
                                order based on this number (lowest first).
                              </p>
                              <div className="font-semibold mt-2">
                                How it works:
                              </div>
                              <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>
                                  <strong>Display order 1</strong> appears first
                                </li>
                                <li>
                                  <strong>Display order 2</strong> appears
                                  second
                                </li>
                                <li>And so on...</li>
                              </ul>
                              <div className="font-semibold mt-2">
                                Best practices:
                              </div>
                              <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>
                                  Use increments of 10 (10, 20, 30) to allow for
                                  easy insertion of new questions later
                                </li>
                                <li>
                                  Questions with the same display order may
                                  appear in any order
                                </li>
                                <li>
                                  This works together with "Next Question" jump
                                  logic for conditional flows
                                </li>
                              </ul>
                            </InfoButton>
                          </div>
                        }
                      >
                        <Input
                          id="display_order"
                          type="number"
                          placeholder="Use this if necessary to order the questions (lower numbers are displayed first)"
                          value={field.state.value}
                          onChange={(e) => {
                            const raw = e.target.valueAsNumber;
                            // Empty number input yields NaN; avoid invalid form state so save stays enabled
                            const n =
                              Number.isNaN(raw) || raw === undefined
                                ? 0
                                : Math.floor(raw);
                            field.handleChange(n);
                          }}
                          onBlur={field.handleBlur}
                          disabled={isReadOnly}
                        />
                      </FormFieldWrapper>
                    )}
                  </form.AppField>

                  <form.AppField
                    name="jump_target"
                    validators={{
                      onChange: QuestionFormBaseSchema.shape.jump_target,
                    }}
                  >
                    {(field) => (
                      <FormFieldWrapper
                        field={field}
                        label={
                          <div className="flex items-center gap-2">
                            <span>Next Question</span>
                            <InfoButton>
                              <div className="font-semibold">
                                Unconditional Jump Logic
                              </div>
                              <p>
                                Control which question appears next after this
                                one, regardless of the user's answer.
                              </p>
                              <div className="font-semibold mt-2">Options:</div>
                              <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>
                                  <strong>Sequential (blank)</strong>: Continue
                                  to the next question in display order
                                </li>
                                <li>
                                  <strong>Display Order (number)</strong>: Skip
                                  ahead to a specific question by its display
                                  order
                                </li>
                                <li>
                                  <strong>End</strong>: Stop the questionnaire
                                  here - no more questions will be shown
                                </li>
                              </ul>
                              <div className="font-semibold mt-2">
                                Example Use Cases:
                              </div>
                              <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>
                                  <strong>Skip sections:</strong> Jump from
                                  display order 10 to 50, skipping intermediate
                                  questions
                                </li>
                                <li>
                                  <strong>Early exit:</strong> Use "End" to
                                  terminate the questionnaire at a specific
                                  point
                                </li>
                                <li>
                                  <strong>Section routing:</strong> Direct all
                                  users to a final summary question
                                </li>
                              </ul>
                              <div className="font-semibold mt-2">
                                Important:
                              </div>
                              <p className="text-muted-foreground">
                                This is an <em>unconditional</em> jump - it
                                applies to all users. For conditional jumps
                                based on answer selection, see the "Next"
                                controls on each answer option below.
                              </p>
                            </InfoButton>
                          </div>
                        }
                      >
                        <div className="flex items-center gap-4">
                          <RadioGroup
                            value={field.state.value === -1 ? "end" : "custom"}
                            onValueChange={(value) => {
                              if (value === "end") {
                                field.handleChange(-1);
                              } else if (value === "custom") {
                                // Switch to custom mode with empty value
                                if (field.state.value === -1) {
                                  field.handleChange(null);
                                }
                              }
                            }}
                            className="flex items-center gap-2"
                            disabled={isReadOnly}
                          >
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="custom" id="jump_custom" />
                              <Input
                                type="number"
                                placeholder="Display Order"
                                value={
                                  field.state.value !== null &&
                                  field.state.value !== -1
                                    ? field.state.value
                                    : ""
                                }
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === "") {
                                    field.handleChange(null);
                                    return;
                                  }
                                  const value = e.target.valueAsNumber;
                                  if (!Number.isNaN(value)) {
                                    field.handleChange(value);
                                  }
                                }}
                                onFocus={() => {
                                  // When input is focused with "End" selected, switch to custom (null = blank/sequential)
                                  if (field.state.value === -1) {
                                    field.handleChange(null);
                                  }
                                }}
                                disabled={isReadOnly}
                                className="w-32"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="end" id="jump_end" />
                              <Label
                                htmlFor="jump_end"
                                className="text-sm font-normal"
                              >
                                End
                              </Label>
                            </div>
                          </RadioGroup>
                        </div>
                      </FormFieldWrapper>
                    )}
                  </form.AppField>

                  {/* Show in list — surfaces the answer as a licenses-list column */}
                  {answerType === "company_contact" ? (
                    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                      <div>
                        <p className="text-sm font-medium">Show in List</p>
                        <p className="text-xs text-muted-foreground">
                          Each switch adds a licenses-list column and filter.
                          Company name shows the person name when the company is
                          blank. Column headers use the question&apos;s bracket
                          label when it has one.
                        </p>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {(
                          [
                            ["show_in_list", "Company name"],
                            ["contact_name_show_in_list", "Person name"],
                            ["contact_email_show_in_list", "Email"],
                            ["contact_phone_show_in_list", "Phone"],
                            ["contact_address_show_in_list", "Address"],
                          ] as const
                        ).map(([name, label]) => (
                          <form.AppField key={name} name={name}>
                            {(field) => (
                              <FormFieldWrapper field={field} label={label}>
                                <div className="flex items-center space-x-2">
                                  <Label className="text-sm">NO</Label>
                                  <Switch
                                    checked={field.state.value ?? false}
                                    onCheckedChange={field.handleChange}
                                    disabled={isReadOnly}
                                  />
                                  <Label className="text-sm">YES</Label>
                                </div>
                              </FormFieldWrapper>
                            )}
                          </form.AppField>
                        ))}
                      </div>
                    </div>
                  ) : (
                    isListableQuestionAnswerType(answerType) && (
                      <form.AppField
                        name="show_in_list"
                        validators={{
                          onChange: QuestionFormBaseSchema.shape.show_in_list,
                        }}
                      >
                        {(field) => (
                          <FormFieldWrapper field={field} label="Show in List">
                            <div className="flex items-center space-x-2">
                              <Label className="text-sm">NO</Label>
                              <Switch
                                id="show_in_list"
                                checked={field.state.value ?? false}
                                onCheckedChange={field.handleChange}
                                disabled={isReadOnly}
                              />
                              <Label className="text-sm">YES</Label>
                              <Label
                                htmlFor="show_in_list"
                                className="text-sm text-muted-foreground ml-2"
                              >
                                {showInListHelpText(answerType)}
                              </Label>
                            </div>
                          </FormFieldWrapper>
                        )}
                      </form.AppField>
                    )
                  )}

                  {!!questionId && isDeactivated && (
                    <Alert variant="warning">
                      <AlertTriangle className="size-6" />
                      <AlertDescription>
                        This question is deactivated. Click Reactivate to
                        restore it and save any changes.
                      </AlertDescription>
                    </Alert>
                  )}
                  {!!questionId &&
                    !isDeactivated &&
                    isAutoGeneratedQuestion(questionData?.question || "") && (
                      <Alert variant="warning">
                        <AlertTriangle className="size-6" />
                        <AlertDescription>
                          This is an auto-generated question used for fee
                          calculation. Deactivating this question may impact fee
                          calculations and prevent the system from calculating
                          fees correctly.
                        </AlertDescription>
                      </Alert>
                    )}
                </form>
              );
            }}
          </form.Subscribe>
        </form.AppForm>
      </CardContent>
      {!isReadOnly && (
        <CardFooter className="flex justify-between gap-2">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.Subscribe
            selector={(state) =>
              [state.isSubmitting, state.values] as [
                boolean,
                typeof state.values,
              ]
            }
          >
            {([isSubmitting, values]) => {
              const hasValues =
                values &&
                typeof values === "object" &&
                "scope" in (values as object);
              const schemaValid =
                hasValues && isQuestionFormSchemaValid(values);
              const showDeactivate = shouldShowQuestionDeactivateButton({
                questionId,
                isDeactivated,
                isReadOnly,
              });
              return (
                <div className="flex gap-2 ml-auto">
                  {showDeactivate && (
                    <Button
                      type="button"
                      variant="outline"
                      className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      onClick={handleDeactivate}
                      disabled={!schemaValid || isSubmitting}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {getQuestionDeactivateButtonLabel(
                        isSubmitting,
                        submitAction,
                      )}
                    </Button>
                  )}
                  <Button
                    className="disabled:opacity-20 disabled:cursor-not-allowed"
                    onClick={(e) => handleSubmit(e)}
                    disabled={isSubmitting}
                  >
                    {isDeactivated && !isSubmitting ? (
                      <RotateCcw className="h-4 w-4 mr-1" />
                    ) : null}
                    {getQuestionFormPrimaryButtonLabel({
                      isSubmitting,
                      questionId,
                      isDeactivated,
                      submitAction,
                    })}
                  </Button>
                </div>
              );
            }}
          </form.Subscribe>
        </CardFooter>
      )}
    </Card>
  );
}
